import type * as SheetPrimitive from "@radix-ui/react-dialog";
import type {
  ButtonHTMLAttributes,
  ComponentPropsWithoutRef,
  ComponentRef,
  HTMLAttributes,
  PropsWithChildren,
  ReactNode,
  RefObject,
} from "react";

import type { TIconKey } from "@/ui/components/primitives/Icon";
import { DefinedDefaults } from "@/ui/lib";
import { TButtonProps, TButtonWidth } from "../../primitives";
import { TTypographyAlign } from "../../typography/typography.config";

/*-------------------------------------
|  Constants
-------------------------------------*/

export const SHEET_CLOSE_ICON: TIconKey = "cross";
export const SHEET_BACK_ICON: TIconKey = "chevronLeft";

export const SHEET_SIDES = [ "right", "left", "top", "bottom" ] as const;

export const SHEET_SURFACES = [ "white", "cream" ] as const;

export const SHEET_CONTENT_VARIANTS = [ "default", "boxed" ] as const;

export const SHEET_CONTENT_CLASS =
  "fixed z-50 flex flex-col gap-4 border-solid border-dialog p-6 shadow-lg outline-none ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500";

export const SHEET_CHILDREN_CLASS =
  "flex flex-1 flex-col gap-4 overflow-y-auto";

export const SHEET_CONTENT_VARIANT_CLASS_MAP: Record<TSheetContentVariant, string> = {
  default: "",
  boxed: "rounded-sys-md border border-default bg-surface-subtle p-4 shadow-sm",
};

export const SHEET_SURFACE_CLASS_MAP: Record<TSheetSurface, string> = {
  white: "bg-surface-default",
  cream: "bg-sys-neutral-600",
} as const;

export const SHEET_SIDE_CLASS_MAP: Record<TSheetSide, string> = {
  right:
    "inset-y-0 right-0 h-full w-full border-l sm:max-w-[36.25rem] data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
  left:
    "inset-y-0 left-0 h-full w-full border-r sm:max-w-[36.25rem] data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left",
  top:
    "inset-x-0 top-0 w-full border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
  bottom:
    "inset-x-0 bottom-0 w-full border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
} as const;

export const SHEET_OVERLAY_CLASS =
  "fixed inset-0 z-40 bg-sys-opacity-dark-20 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0";

export const SHEET_HEADER_CLASS = "flex w-full items-start gap-2";

export const SHEET_HEADER_TEXT_CLASS = "flex min-w-0 flex-1 flex-col";

export const SHEET_CLOSE_CLASS =
  "absolute right-4 top-4 h-6 w-6 rounded-sys-sm text-foreground opacity-70 transition-opacity hover:opacity-100 focus:outline focus:outline-2 focus:outline-offset-0 focus:outline-sys-chartreuse-600 disabled:pointer-events-none";

export const SHEET_BACK_CLASS =
  "flex size-7 shrink-0 items-center justify-center rounded-sys-sm text-foreground opacity-70 transition-opacity hover:opacity-100 focus:outline focus:outline-2 focus:outline-offset-0 focus:outline-sys-chartreuse-600 disabled:pointer-events-none";

export const SHEET_TYPOGRAPHY_ALIGN_PROPS: Record<TSheetAlignContent, TSheetAlignContent> = {
  left: "left",
  center: "center",
} as const;

export const SHEET_FOOTER_CTAS_DIRECTION_CLASS_MAP: Record<TSheetAlignContent, string> = {
  left: "flex-row-reverse justify-start",
  center: "flex-col justify-center",
} as const;

export const SHEET_FOOTER_CTA_DIRECTION_BUTTON_WIDTH_MAP: Record<
  TSheetAlignContent, TButtonWidth
> = {
  left: "content",
  center: "fill",
};

/*-------------------------------------
|  Types
-------------------------------------*/

export type TSheetSide = (typeof SHEET_SIDES)[number];

export type TSheetSurface = (typeof SHEET_SURFACES)[number];

export type TSheetContentVariant = (typeof SHEET_CONTENT_VARIANTS)[number];

export type TSheetAlignContent = Extract<TTypographyAlign, "left" | "center">;

export type TSheetAlignProps = {
  align?: TSheetAlignContent;
};

export type TSheetRootProps = ComponentPropsWithoutRef<typeof SheetPrimitive.Root> &
  PropsWithChildren &
  TSheetAlignProps & {
    title: string;
    description: string;
    hideDescription?: boolean;
    side?: TSheetSide;
    surface?: TSheetSurface;
    contentVariant?: TSheetContentVariant;
    contentClassName?: string;
    footer?: TSheetFooterProps;
    onBack?: () => void;
    backLabel?: string;
    close?: ReactNode;
    returnFocusRef?: RefObject<HTMLElement | null>;
  };
export type TSheetFooterCtas = {
  primary?: TButtonProps;
  secondary?: TButtonProps;
};
export type TSheetFooterCtaProps = TSheetAlignProps & {
  ctas?: TSheetFooterCtas;
};
export type TSheetTriggerElement = ComponentRef<typeof SheetPrimitive.Trigger>;
export type TSheetTriggerProps = ComponentPropsWithoutRef<typeof SheetPrimitive.Trigger>;
export type TSheetPortalProps = ComponentPropsWithoutRef<typeof SheetPrimitive.Portal>;
export type TSheetOverlayElement = ComponentRef<typeof SheetPrimitive.Overlay>;
export type TSheetOverlayProps = ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>;
export type TSheetContentElement = ComponentRef<typeof SheetPrimitive.Content>;
export type TSheetContentProps = ComponentPropsWithoutRef<typeof SheetPrimitive.Content> & {
  close?: ReactNode;
  side?: TSheetSide;
  surface?: TSheetSurface;
};
export type TSheetCloseElement = ComponentRef<typeof SheetPrimitive.Close>;
export type TSheetCloseProps = ComponentPropsWithoutRef<typeof SheetPrimitive.Close>;
export type TSheetTitleElement = ComponentRef<typeof SheetPrimitive.Title>;
export type TSheetTitleProps = TSheetAlignProps & ComponentPropsWithoutRef<typeof SheetPrimitive.Title>;
export type TSheetDescriptionElement = ComponentRef<typeof SheetPrimitive.Description>;
export type TSheetDescriptionProps = {
  hideDescription?: boolean;
} & TSheetAlignProps & ComponentPropsWithoutRef<
  typeof SheetPrimitive.Description
>;
export type TSheetHeaderProps = HTMLAttributes<HTMLDivElement>;
export type TSheetChildrenProps = PropsWithChildren<{
  contentVariant?: TSheetContentVariant;
}>;
export type TSheetFooterProps = HTMLAttributes<HTMLDivElement> & TSheetFooterCtaProps;
export type TSheetBackProps = ButtonHTMLAttributes<HTMLButtonElement>;

type TSheetRootDefaultProps = DefinedDefaults<
  TSheetRootProps,
  "align" | "side" | "surface" | "contentVariant"
>;

type TSheetTitleDefaultProps = DefinedDefaults<
  TSheetTitleProps,
  "align"
>;

type TSheetDescriptionDefaultProps = DefinedDefaults<
  TSheetDescriptionProps,
  "align"
>;

type TSheetFooterCtaDefaultProps = DefinedDefaults<
  TSheetFooterCtaProps,
  "align"
>;

/*-------------------------------------
|  Defaults
|-------------------------------------*/

export const SHEET_ROOT_DEFAULTS = {
  align: "left",
  side: "right",
  surface: "white",
  contentVariant: "default",
} satisfies TSheetRootDefaultProps;

export const SHEET_TITLE_DEFAULTS = {
  align: "left",
} satisfies TSheetTitleDefaultProps;

export const SHEET_DESCRIPTION_DEFAULTS = {
  align: "left",
} satisfies TSheetDescriptionDefaultProps;

export const SHEET_FOOTER_CTAS_DEFAULTS = {
  align: "left",
} satisfies TSheetFooterCtaDefaultProps;
